
import React, { useState } from 'react';
import { User, Mail, Lock, Loader2 } from './Icons';
import { ViewState } from '../types';
import { BrandLogo } from './BrandLogo';

interface AuthProps {
  view: 'login' | 'signup';
  onNavigate: (view: ViewState) => void;
  onSubmit: (data: any) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const AuthForm: React.FC<AuthProps> = ({ view, onNavigate, onSubmit, isLoading, error }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (view === 'signup') {
      onSubmit({ name, email, password });
    } else {
      onSubmit({ email, password });
    }
  };

  return (
    <div className="w-full max-w-md mx-auto mt-6 px-4 animate-slide-up pb-20">
      <div className="text-center mb-8 flex flex-col items-center">
        {/* Logo Component */}
        <div className="h-40 w-40 mb-2 drop-shadow-[0_0_15px_rgba(222,160,30,0.4)] hover:scale-105 transition-transform duration-500 flex justify-center">
            <BrandLogo className="w-full h-full" />
        </div>
        <h2 className="text-3xl font-serif font-bold mb-2">{view === 'login' ? 'Welcome Back!' : 'Join the Forge'}</h2>
        <p className="text-gray-400">
          {view === 'login' 
            ? 'Sign in to access your library of adventures.' 
            : 'Create an account to start forging magical stories.'}
        </p>
      </div>

      <div className="glass-panel p-8 rounded-3xl shadow-2xl border border-white/5 relative overflow-hidden">
        {/* Texture */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>

        {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 text-red-200 text-sm rounded-xl text-center relative z-10">
                {error}
            </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
          {view === 'signup' && (
            <div className="space-y-1">
              <label htmlFor="full_name" className="text-xs font-bold text-gold-500/80 uppercase tracking-wider ml-1">Name</label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-gold-400 transition-colors">
                    <User className="w-5 h-5" />
                </div>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  className="w-full bg-black/40 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-white focus:border-gold-500 focus:ring-1 focus:ring-gold-500/50 focus:outline-none transition-all placeholder-gray-600"
                  placeholder="Your Name"
                  required
                />
              </div>
            </div>
          )}

          <div className="space-y-1">
            <label htmlFor="email" className="text-xs font-bold text-gold-500/80 uppercase tracking-wider ml-1">Email</label>
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-gold-400 transition-colors">
                  <Mail className="w-5 h-5" />
              </div>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="w-full bg-black/40 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-white focus:border-gold-500 focus:ring-1 focus:ring-gold-500/50 focus:outline-none transition-all placeholder-gray-600"
                placeholder="you@example.com"
                required
              />
            </div>
          </div>

          <div className="space-y-1">
            <label htmlFor="password" className="text-xs font-bold text-gold-500/80 uppercase tracking-wider ml-1">Password</label>
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-gold-400 transition-colors">
                  <Lock className="w-5 h-5" />
              </div>
              <input
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={view === 'login' ? 'current-password' : 'new-password'}
                className="w-full bg-black/40 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-white focus:border-gold-500 focus:ring-1 focus:ring-gold-500/50 focus:outline-none transition-all placeholder-gray-600"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 btn-primary-gradient text-white rounded-xl font-bold shadow-lg shadow-forge-500/20 transition-all flex justify-center items-center gap-2 mt-8 hover:scale-[1.02] active:scale-[0.98]"
          >
            {isLoading ? <Loader2 className="animate-spin w-5 h-5" /> : (view === 'login' ? 'Log In' : 'Sign Up')}
          </button>
        </form>

        <div className="mt-8 text-center text-sm text-gray-400 relative z-10">
          {view === 'login' ? (
            <p>
              Don't have an account?{' '}
              <button 
                onClick={() => onNavigate('signup')}
                className="text-gold-400 hover:text-white font-bold transition-colors underline decoration-gold-400/30 underline-offset-4"
              >
                Sign Up
              </button>
            </p>
          ) : (
             <p>
              Already have an account?{' '}
              <button 
                onClick={() => onNavigate('login')}
                className="text-gold-400 hover:text-white font-bold transition-colors underline decoration-gold-400/30 underline-offset-4"
              >
                Log In
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthForm;
